/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownProps } from 'react-markdown';

import useRouter from 'js/lib/useRouter';

import { onClickNavUrl } from 'bundles/cds-labs/components/Modules/utils/utm';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { sharedMarkdownStyles } from 'bundles/ent-website/shared-styles';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import isExternalLink from 'bundles/ent-website/utils/isExternalLink';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

const styles = {
  markdown: sharedMarkdownStyles,
};

type Props = Omit<ReactMarkdownProps, 'source'> & {
  module: BlockLevelModule;
  source: string | null | undefined; // Override to handle Maybe types
};

type LinkProps = {
  href: string;
  text: string;
  module: BlockLevelModule;
  namespace?: string;
};

export const LinkRenderer: React.LegacyFunctionComponentWithChildren<LinkProps> = ({
  href,
  namespace,
  children,
  module,
}) => {
  const target = isExternalLink(href) ? '_blank' : '_self';
  const rel = isExternalLink(href) ? 'noopener noreferrer' : '';
  const trackingName = eventingTransform(`${module.__typename}_${module.slug}_${namespace || 'link'}`);
  const router = useRouter();

  return (
    <TrackedA href={href} onClick={onClickNavUrl(router, href)} trackingName={trackingName} rel={rel} target={target}>
      {children}
    </TrackedA>
  );
};

function Markdown(props: Props) {
  const { renderers, module, source } = props;

  if (!source) {
    return null;
  }

  const linkWrapper: React.LegacyFunctionComponentWithChildren<HTMLAnchorElement> = (linkProps) => {
    return <LinkRenderer {...linkProps} module={module} />;
  };

  const customRenderers = {
    link: linkWrapper,
    ...renderers,
  };

  return <ReactMarkdown renderers={customRenderers} {...props} source={source} css={styles.markdown} />;
}

export default Markdown;
